import { Alert, Avatar, Button, Card, Form, Input, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inviteAdminUser, listAdminUsers, resendAdminUserInvitation, updateAdminUserActive } from '../services/api';
import type { AdminUser, AdminUserInviteCreate, AdminUserStatus, CurrentUser } from '../types/incident';

const { Text } = Typography;

type UserManagementPageProps = {
  currentUser: CurrentUser | null;
};

const statusColors: Record<AdminUserStatus, string> = {
  INVITED: 'gold',
  ACTIVE: 'green',
  DISABLED: 'default',
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const userInitial = (user: AdminUser): string => {
  const name = user.display_name || user.email;
  return name.trim().charAt(0).toUpperCase() || '?';
};

const UserManagementPage = ({ currentUser }: UserManagementPageProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AdminUserInviteCreate>();
  const [messageApi, contextHolder] = message.useMessage();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [updatingUsername, setUpdatingUsername] = useState<string | null>(null);
  const [resendingUsername, setResendingUsername] = useState<string | null>(null);

  const isSystemAdmin = useMemo(
    () => currentUser?.assignments.some((assignment) => assignment.role === 'SYSTEM_ADMIN' && assignment.is_active) || false,
    [currentUser?.assignments]
  );

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await listAdminUsers());
    } catch {
      messageApi.error(t('adminUsers.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [messageApi, t]);

  useEffect(() => {
    if (isSystemAdmin) {
      loadUsers();
    } else {
      setLoading(false);
    }
  }, [isSystemAdmin, loadUsers]);

  const handleInvite = async (values: AdminUserInviteCreate) => {
    setInviteLoading(true);
    try {
      const invited = await inviteAdminUser(values);
      setUsers((current) => [invited, ...current.filter((user) => user.email.toLowerCase() !== invited.email.toLowerCase())]);
      form.resetFields();
      messageApi.success(t('adminUsers.messages.invited'));
      await loadUsers();
    } catch {
      messageApi.error(t('adminUsers.messages.inviteFailed'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleActiveChange = async (user: AdminUser, isActive: boolean) => {
    if (!user.cognito_username) {
      messageApi.error(t('adminUsers.messages.missingCognitoUser'));
      return;
    }
    setUpdatingUsername(user.cognito_username);
    try {
      const updated = await updateAdminUserActive(user.cognito_username, { is_active: isActive });
      setUsers((current) => current.map((item) => (item.email === updated.email ? updated : item)));
      messageApi.success(isActive ? t('adminUsers.messages.enabled') : t('adminUsers.messages.disabled'));
      await loadUsers();
    } catch {
      messageApi.error(t('adminUsers.messages.updateFailed'));
    } finally {
      setUpdatingUsername(null);
    }
  };

  const handleResendInvitation = async (user: AdminUser) => {
    if (!user.cognito_username) {
      messageApi.error(t('adminUsers.messages.missingCognitoUser'));
      return;
    }
    setResendingUsername(user.cognito_username);
    try {
      const updated = await resendAdminUserInvitation(user.cognito_username);
      setUsers((current) => current.map((item) => (item.email === updated.email ? { ...item, ...updated } : item)));
      messageApi.success(t('adminUsers.messages.resent'));
      await loadUsers();
    } catch {
      messageApi.error(t('adminUsers.messages.resendFailed'));
    } finally {
      setResendingUsername(null);
    }
  };

  const columns: ColumnsType<AdminUser> = [
    {
      title: t('adminUsers.table.user'),
      dataIndex: 'email',
      key: 'email',
      width: 320,
      render: (_value, user) => (
        <Space align="center" size={10}>
          <Avatar src={user.profile_image_url || undefined}>{userInitial(user)}</Avatar>
          <Space direction="vertical" size={2}>
            <Text strong>{user.display_name || user.email}</Text>
            <Text type="secondary">{user.email}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: t('adminUsers.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (statusValue: AdminUserStatus) => (
        <Tag color={statusColors[statusValue]}>{t(`adminUsers.status.${statusValue}`)}</Tag>
      ),
    },
    {
      title: t('adminUsers.table.assignments'),
      dataIndex: 'assignments',
      key: 'assignments',
      width: 180,
      render: (_value, user) => (
        <Space wrap size={[4, 4]}>
          {user.assignments.length > 0 ? (
            user.assignments.map((assignment) => <Tag key={assignment.id}>{assignment.label}</Tag>)
          ) : (
            <Text type="secondary">{t('adminUsers.table.noAssignments')}</Text>
          )}
        </Space>
      ),
    },
    {
      title: t('adminUsers.table.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: formatDateTime,
    },
    {
      title: t('adminUsers.table.lastLoginAt'),
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 160,
      render: formatDateTime,
    },
    {
      title: t('adminUsers.table.actions'),
      key: 'actions',
      width: 220,
      render: (_value, user) => {
        const isUpdating = Boolean(user.cognito_username && updatingUsername === user.cognito_username);
        const isResending = Boolean(user.cognito_username && resendingUsername === user.cognito_username);
        if (!user.cognito_username) {
          return <Text type="secondary">{t('adminUsers.actions.cognitoMissing')}</Text>;
        }
        if (user.status === 'DISABLED') {
          return (
            <Button size="small" loading={isUpdating} disabled={isUpdating} onClick={() => handleActiveChange(user, true)}>
              {t('adminUsers.actions.enable')}
            </Button>
          );
        }
        if (user.status === 'INVITED') {
          return (
            <Space size={6} wrap>
              <Button size="small" loading={isResending} disabled={isResending || isUpdating} onClick={() => handleResendInvitation(user)}>
                {t('adminUsers.actions.resendInvitation')}
              </Button>
              <Popconfirm
                title={t('adminUsers.actions.disableConfirmTitle')}
                description={t('adminUsers.actions.disableConfirmDescription')}
                okText={t('common.yes')}
                cancelText={t('common.cancel')}
                onConfirm={() => handleActiveChange(user, false)}
              >
                <Button danger size="small" loading={isUpdating} disabled={isUpdating || isResending}>
                  {t('adminUsers.actions.disable')}
                </Button>
              </Popconfirm>
            </Space>
          );
        }
        return (
          <Popconfirm
            title={t('adminUsers.actions.disableConfirmTitle')}
            description={t('adminUsers.actions.disableConfirmDescription')}
            okText={t('common.yes')}
            cancelText={t('common.cancel')}
            onConfirm={() => handleActiveChange(user, false)}
          >
            <Button danger size="small" loading={isUpdating} disabled={isUpdating}>
              {t('adminUsers.actions.disable')}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  if (!isSystemAdmin) {
    return (
      <div className="page">
        {contextHolder}
        <Alert type="warning" showIcon message={t('adminUsers.forbidden')} />
      </div>
    );
  }

  return (
    <div className="page adminUsersPage">
      {contextHolder}
      <div className="pageHeader">
        <p>{t('adminUsers.description')}</p>
      </div>

      <Card className="adminInviteCard" title={t('adminUsers.invite.title')}>
        <Form form={form} layout="inline" onFinish={handleInvite}>
          <Form.Item
            name="email"
            label={t('adminUsers.invite.email')}
            rules={[
              { required: true },
              { type: 'email', message: t('adminUsers.invite.emailInvalid') },
            ]}
          >
            <Input className="adminInviteEmailInput" placeholder="name@example.com" />
          </Form.Item>
          <Form.Item name="display_name" label={t('adminUsers.invite.displayName')}>
            <Input className="adminInviteNameInput" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={inviteLoading}>
              {t('adminUsers.invite.submit')}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card className="adminUsersTableCard" title={t('adminUsers.listTitle')}>
        <Table
          rowKey={(user) => user.cognito_username || user.id || user.email}
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );
};

export default UserManagementPage;
